<template>
  <validation-observer
    ref="ticketForm"
    slim
  >
    <b-form
      class="enhanced-ticket-form"
      @submit.prevent="submitTicket"
    >
      <!-- Ticket Type Display -->
      <b-form-group class="form-group-enhanced">
        <label class="form-label-enhanced mb-0">
          <feather-icon
            icon="TagIcon"
            size="18"
            class="mr-75"
          />
          Type de Ticket
          <b-badge
            pill
            size="lg"
            class="ticket-type-badge ml-1"
          >
            <feather-icon
              :icon="ticketTypeIcon"
              class="mr-50"
              size="18"
            />
            {{ ticketTypeLabel }}
          </b-badge>
        </label>
      </b-form-group>

      <!-- Title -->
      <validation-provider
        #default="{ errors }"
        name="Titre"
        rules="required|min:10"
        mode="aggressive"
      >
        <b-form-group class="form-group-enhanced">
          <label
            class="form-label-enhanced"
            for="title"
          >
            <feather-icon
              icon="TypeIcon"
              size="18"
              class="mr-75"
            />
            Titre *
          </label>
          <b-input-group>
            <b-input-group-prepend is-text>
              <feather-icon
                icon="EditIcon"
                size="16"
              />
            </b-input-group-prepend>
            <b-form-input
              id="title"
              v-model="form.title"
              placeholder="Bref résumé du problème"
              :state="errors.length > 0 ? false : null"
              class="enhanced-input"
            />
          </b-input-group>
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>

      <!-- Description -->
      <validation-provider
        #default="{ errors }"
        name="Description"
        rules="required|min:20"
        mode="aggressive"
      >
        <b-form-group class="form-group-enhanced">
          <label
            class="form-label-enhanced"
            for="description"
          >
            <feather-icon
              icon="FileTextIcon"
              size="18"
              class="mr-75"
            />
            Description *
          </label>
          <b-form-textarea
            id="description"
            v-model="form.description"
            placeholder="Fournissez des informations détaillées sur le problème ou la demande"
            rows="5"
            :state="errors.length > 0 ? false : null"
            class="enhanced-textarea"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>

      <!-- Priority -->
      <b-form-group class="form-group-enhanced priority-group">
        <label class="form-label-enhanced">
          <feather-icon
            icon="AlertCircleIcon"
            size="18"
            class="mr-75"
          />
          Priorité *
        </label>
        <div class="priority-options">
          <div
            v-for="option in priorityOptions"
            :key="option.value"
            class="priority-option"
            :class="{ active: form.priority === option.value }"
            @click="form.priority = option.value"
          >
            <b-form-radio
              v-model="form.priority"
              :value="option.value"
              class="priority-radio"
            >
              <div class="priority-content">
                <feather-icon
                  :icon="getPriorityIcon(option.value)"
                  size="20"
                  :class="`priority-icon priority-${option.value}`"
                />
                <span class="priority-text">{{ option.text }}</span>
              </div>
            </b-form-radio>
          </div>
        </div>
      </b-form-group>

      <!-- Actions -->
      <div class="form-actions">
        <b-button
          variant="outline-secondary"
          class="action-btn cancel-btn"
          size="lg"
          @click="$emit('cancel')"
        >
          <feather-icon
            icon="XIcon"
            class="mr-50"
          />
          Annuler
        </b-button>
        <b-button
          variant="primary"
          class="action-btn submit-btn"
          size="lg"
          :disabled="loading"
          @click.prevent="handleSubmitClick"
        >
          <b-spinner
            v-if="loading"
            small
            class="mr-50"
          />
          <feather-icon
            v-else
            icon="SendIcon"
            class="mr-50"
          />
          {{ loading ? 'Création...' : 'Soumettre le Ticket' }}
        </b-button>
      </div>
    </b-form>
  </validation-observer>
</template>

<script>
import {
  BForm, BFormGroup, BFormInput, BFormTextarea, BFormRadio,
  BButton, BSpinner, BBadge, BInputGroup, BInputGroupPrepend,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
// eslint-disable-next-line no-unused-vars
import { required, min } from '@/@core/utils/validations/validations'
import axios from '@/libs/axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'TicketForm',
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormRadio,
    BButton,
    BSpinner,
    BBadge,
    BInputGroup,
    BInputGroupPrepend,
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    ticketType: {
      type: String,
      required: true,
      validator: val => ['bug', 'feature', 'technical'].includes(val),
    },
  },
  data() {
    return {
      form: {
        ticket_type: this.ticketType,
        title: '',
        description: '',
        priority: 'medium',
      },
      loading: false,
      priorityOptions: [
        { text: 'Faible - Problème mineur', value: 'low' },
        { text: 'Moyen - Affecte certaines opérations', value: 'medium' },
        { text: 'Élevé - Affecte de nombreuses opérations', value: 'high' },
        { text: 'Critique - Opérations commerciales arrêtées', value: 'critical' },
      ],
    }
  },
  computed: {
    ticketTypeLabel() {
      const labels = {
        bug: 'Signalement de Bug',
        feature: 'Demande de Fonctionnalité',
        technical: 'Support Technique',
      }
      return labels[this.ticketType] || this.ticketType
    },
    ticketTypeIcon() {
      const icons = {
        bug: 'AlertOctagonIcon',
        feature: 'LightbulbIcon',
        technical: 'MessageCircleIcon',
      }
      return icons[this.ticketType] || 'HelpCircleIcon'
    },
    ticketTypeVariant() {
      const variants = {
        bug: 'danger',
        feature: 'primary',
        technical: 'success',
      }
      return variants[this.ticketType] || 'secondary'
    },
  },
  methods: {
    getPriorityIcon(priority) {
      const icons = {
        low: 'CheckCircleIcon',
        medium: 'AlertCircleIcon',
        high: 'AlertTriangleIcon',
        critical: 'AlertOctagonIcon',
      }
      return icons[priority] || 'AlertCircleIcon'
    },
    handleSubmitClick(event) {
      console.log('=== BUTTON CLICKED ===')
      console.log('Event:', event)
      console.log('Loading state:', this.loading)
      this.submitTicket()
    },
    async submitTicket() {
      console.log('submitTicket called')
      console.log('Form data:', this.form)

      const valid = await this.$refs.ticketForm.validate()
      console.log('Validation result:', valid)

      if (!valid) {
        console.log('Validation failed')
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation échouée',
            text: 'Veuillez remplir tous les champs requis correctement',
            variant: 'warning',
            icon: 'AlertCircleIcon',
          },
        })
        return
      }

      this.loading = true
      console.log('Sending request to API...')

      try {
        const response = await axios.post('/api/support/supplier-tickets/', this.form)
        console.log('Ticket created successfully:', response.data)

        // Reset form after successful creation
        this.form.title = ''
        this.form.description = ''
        this.form.priority = 'medium'

        // Reset validation
        this.$nextTick(() => {
          this.$refs.ticketForm.reset()
        })

        // Emit event to parent component (parent will show the toast)
        this.$emit('ticket-created', response.data)
      } catch (error) {
        console.error('Error creating ticket:', error)
        console.error('Error response:', error.response)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erreur',
            text: error.response?.data?.detail || error.response?.data?.message || 'Erreur lors de la création du ticket',
            variant: 'danger',
            icon: 'AlertCircleIcon',
          },
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.enhanced-ticket-form {
  .form-group-enhanced {
    margin-bottom: 1.75rem;

    .form-label-enhanced {
      font-weight: 600;
      font-size: 1rem;
      color: #5e5873;
      display: flex;
      align-items: center;
      margin-bottom: 0.75rem;

      svg {
        color: #000000;
      }
    }
  }

  .ticket-type-badge {
    font-size: 0.95rem;
    padding: 0.6rem 1.25rem;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    box-shadow: none;
    border: none !important;
    /* Ensure only this background is applied */
    background: rgba(237, 25, 25, 0.1) !important;
    background-color: rgba(237, 25, 25, 0.1) !important;
    color: #ED1919 !important;
  }

  .enhanced-input,
  .enhanced-textarea {
    border-radius: 0.5rem;
    border: 1.5px solid #d8d6de;
    font-size: 0.95rem;
    transition: all 0.2s ease;

    &:focus {
      border-color: #7367f0;
      box-shadow: 0 3px 10px 0 rgba(115, 103, 240, 0.15);
    }
  }

  .enhanced-textarea {
    resize: vertical;
    min-height: 120px;
  }

  .input-group-text {
    background: #f8f8f8;
    border: 1.5px solid #d8d6de;
    border-right: none;
    color: #7367f0;
  }

  .priority-group {
    .priority-options {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .priority-option {
      border: 2px solid #e8e8e8;
      border-radius: 0.75rem;
      padding: 1rem 1.25rem;
      cursor: pointer;
      transition: all 0.2s ease;
      background: white;

      &:hover {
        border-color: #7367f0;
        background: #f8f7fc;
        transform: translateX(4px);
      }

      &.active {
        border-color: #7367f0;
        background: linear-gradient(135deg, #f8f7fc 0%, #ffffff 100%);
        box-shadow: 0 3px 12px rgba(115, 103, 240, 0.2);

        .priority-icon {
          transform: scale(1.1);
        }
      }

      .priority-radio {
        margin: 0;
        width: 100%;

        ::v-deep .custom-control-label {
          width: 100%;
          cursor: pointer;
        }
      }

      .priority-content {
        display: flex;
        align-items: center;
        gap: 0.75rem;

        .priority-icon {
          transition: all 0.2s ease;

          &.priority-low {
            color: #28c76f;
          }

          &.priority-medium {
            color: #ff9f43;
          }

          &.priority-high {
            color: #ff6b6b;
          }

          &.priority-critical {
            color: #ea5455;
          }
        }

        .priority-text {
          font-size: 0.95rem;
          font-weight: 500;
          color: #5e5873;
        }
      }
    }
  }

  .form-actions {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 2px solid #f3f2f7;

    .action-btn {
      padding: 0.75rem 2rem;
      font-weight: 600;
      border-radius: 0.5rem;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;

      &.cancel-btn {
        &:hover {
          background: #f8f8f8;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
      }

      &.submit-btn {
        background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
        border: none;
        box-shadow: 0 4px 15px rgba(115, 103, 240, 0.3);

        &:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(115, 103, 240, 0.4);
        }

        &:active:not(:disabled) {
          transform: translateY(0);
        }

        &:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
      }
    }
  }
}
</style>
